import classNames from 'classnames';
import React from 'react';

import AnchorLink from '@jpp/atoms/AnchorLink/AnchorLink';
import Heading from '@jpp/atoms/Heading';
import RefImage from '@jpp/atoms/Image';
import { IPostStoreState } from 'common/redux/post/reducer';
import { EPageType } from 'common/typings/enums';

import './BlogCard.scss';
import Related from './components/Related';
import Standard from './components/Standard';

export enum EBlogCardVariant {
  Standard,
  Related,
}

export interface IBlogCardProps {
  className?: string;
  post?: IPostStoreState;
  variant?: EBlogCardVariant;
  featured?: boolean;
}

const ROOT_CLASSNAME = 'BlogCard';

const variantMap = new Map<
  EBlogCardVariant,
  React.ComponentType<IBlogCardProps>
>();
variantMap.set(EBlogCardVariant.Standard, Standard);
variantMap.set(EBlogCardVariant.Related, Related);

const BlogCard: React.FunctionComponent<IBlogCardProps> = ({
  className,
  post,
  variant = EBlogCardVariant.Standard,
  featured = false,
}) => {
  if (!post) {
    return null;
  }

  const { post_title, media, excerpt, slug, category_names, featured_image } =
    post;
  const imageSrc = media?.large ?? featured_image?.url;
  const title = post_title ?? (post?.title as any)?.rendered ?? post.title;
  const category = category_names && category_names[0];
  const Component = variantMap.get(variant);

  if (!Component) {
    return null;
  }

  return (
    <Component
      className={classNames(ROOT_CLASSNAME, className, {
        [`${ROOT_CLASSNAME}--is-featured`]: featured,
      })}
    >
      <AnchorLink
        className={`${ROOT_CLASSNAME}__link`}
        href={`/${EPageType.Blog}/${slug}`}
      >
        <span className={`${ROOT_CLASSNAME}__link-text`}>{title}</span>
      </AnchorLink>

      {imageSrc && (
        <div className={`${ROOT_CLASSNAME}__image-wrapper`}>
          <RefImage
            className={`${ROOT_CLASSNAME}__image`}
            image={
              {
                url: imageSrc,
                alt: `${title} image`,
                width: 500,
                height: 625,
              } as Core.IMedia
            }
          />
        </div>
      )}

      <div className={`${ROOT_CLASSNAME}__content-wrapper`}>
        <Heading
          className={`${ROOT_CLASSNAME}__heading`}
          priority={3}
          children={title}
        />

        {featured && excerpt && (
          <p
            className={`${ROOT_CLASSNAME}__copy`}
            dangerouslySetInnerHTML={{
              __html: (excerpt as any)?.rendered ?? excerpt,
            }}
          />
        )}

        {category && (
          <p className={`${ROOT_CLASSNAME}__category`}>{category}</p>
        )}
      </div>
    </Component>
  );
};

export default BlogCard;
